import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { FiPlus, FiUpload } from 'react-icons/fi';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { AppListLayout } from '@/shared/app-list-layout';
import type { DataTableParams, DataTableView } from '@/shared/app-list-layout';
import type { ListColumn } from '@/shared/app-list-layout/types';
import { TenantApi } from '@/modules/tenants/services/api';
import type { Tenant } from '@/api/schemas/tenants';
import type { BadgeTone } from '@/shared/ui/badge';
import { TenantRowActions } from './parts/tenant-row-actions';

/** Status options surfaced in the column-bound filter dropdown. */
const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'closed', label: 'Closed' },
];

/** Maps the contract `status` to a Badge tone and human label. */
const STATUS_TONE: Record<Tenant['status'], { tone: BadgeTone; label: string }> = {
  active: { tone: 'positive', label: 'Active' },
  suspended: { tone: 'caution', label: 'Suspended' },
  closed: { tone: 'neutral', label: 'Closed' },
};

/**
 * Per-view counts, counted from the registry read that answered the query.
 *
 * A key is present only when a read observed it, so a status with no rows shows
 * no badge rather than an invented "0".
 */
type ViewCounts = Partial<Record<'all' | Tenant['status'], number>>;

/**
 * The one failure the page reports. The page does not restate the server's
 * reason; a failed read is a failed read.
 */
const READ_FAILED = 'These results could not be loaded.';

/**
 * The first query, in the table's own defaults.
 *
 * The table is only mounted once a read has settled, so it cannot ask the first
 * question itself. The page asks it here; the engine's mount announcement of the
 * same query is answered from the same read (see `useFetchHandler`).
 */
const INITIAL_QUERY: DataTableParams = { page: 1, pageSize: 25, sort: [], filters: {} };

/** Saved views rendered as tabs above the tenant list. */
function buildTenantViews(counts: ViewCounts): DataTableView[] {
  return [
    { id: 'all', label: 'All tenants', count: counts.all },
    { id: 'active', label: 'Active', filters: { status: 'active' }, count: counts.active },
    {
      id: 'suspended',
      label: 'Suspended',
      filters: { status: 'suspended' },
      count: counts.suspended,
    },
  ];
}

/** Counts every saved view from the same rows the query was answered from. */
function countTenants(tenants: Tenant[]): ViewCounts {
  const counts: ViewCounts = { all: tenants.length };
  for (const tenant of tenants) {
    counts[tenant.status] = (counts[tenant.status] ?? 0) + 1;
  }
  return counts;
}

/** Column descriptors for the tenant list. */
const TENANT_COLUMNS: ReadonlyArray<ListColumn<Tenant>> = [
  { id: 'slug', header: 'Tenant', cell: (row) => row.slug, sortable: true },
  {
    id: 'status',
    header: 'Status',
    cell: (row) => <TenantStatusBadge status={row.status} />,
    sortable: true,
    filter: { type: 'select', options: STATUS_OPTIONS, placeholder: 'Any' },
  },
  {
    id: 'createdAt',
    header: 'Created',
    sortable: true,
    cell: (row) => (
      <span className="whitespace-nowrap">
        {new Date(row.createdAt).toLocaleDateString()}
        <span className="ml-1.5 text-content-muted">
          {new Date(row.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </span>
    ),
  },
  {
    id: 'actions',
    header: '',
    sticky: 'right',
    cell: (row, context) => (
      <TenantRowActions row={row} variant={context?.variant === 'sheet' ? 'sheet' : 'inline'} />
    ),
  },
];

/**
 * Tenants directory.
 *
 * Renders through the shared `AppListLayout` with the full UX surface: saved-view
 * tabs with counts, search, a status column filter, sticky actions column,
 * numeric pagination, and table/cards view toggle.
 *
 * The data comes from the platform's tenant registry over HTTP. The registry is
 * small and bounded — one row per merchant this deployment hosts — so the page
 * adapter applies page, sort, search and filters locally over the registry it
 * read. `onFetch` stays the single data path, so moving the work server-side is
 * a change to one function.
 */
export function ListPage(): ReactNode {
  const [rows, setRows] = useState<Tenant[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<ViewCounts>({});
  const [searchValue, setSearchValue] = useState('');
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error'>('loading');

  const onFetch = useFetchHandler(setRows, setTotal, setCounts, setPhase);

  useEffect(() => {
    onFetch(INITIAL_QUERY);
  }, [onFetch]);

  const views = useMemo(() => buildTenantViews(counts), [counts]);

  return (
    <AppListLayout<Tenant>
      title="Tenants"
      description="A provisioned tenant is the boundary that contains every other entity."
      toolbarActions={
        <>
          <ExportButton />
          <NewTenantButton />
        </>
      }
      columns={TENANT_COLUMNS}
      rows={rows}
      total={total}
      getRowId={(row) => row.id}
      views={views}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      onFetch={onFetch}
      isLoading={phase === 'loading'}
      error={phase === 'error' ? new Error(READ_FAILED) : null}
      emptyTitle="No tenants yet"
      emptyDescription="A provisioned tenant is the boundary that contains every other entity."
      errorMessage={READ_FAILED}
    />
  );
}

/** Stable identity for one query, so the same question is never read twice. */
function queryKey(params: DataTableParams): string {
  return JSON.stringify([params.page, params.pageSize, params.sort, params.filters]);
}

/**
 * Builds the `onFetch` handler that answers `DataTableParams` against the
 * registry — and derives the saved-view counts from the SAME read.
 *
 * One read per question is what makes the count rule literally true: the counts
 * travel with the rows the query was answered from, so a failed read is the
 * page's error surface rather than a tab strip of zeros.
 */
function useFetchHandler(
  setRows: (rows: Tenant[]) => void,
  setTotal: (total: number) => void,
  setCounts: (counts: ViewCounts) => void,
  setPhase: (phase: 'ready' | 'error') => void,
): (params: DataTableParams) => void {
  const answered = useRef<string | null>(null);
  const accumulated = useRef<Tenant[]>([]);

  return useCallback(
    (params: DataTableParams): void => {
      const key = queryKey(params);
      // The page asks the first query before the table exists; the table
      // announces that same query when it mounts. One question, one read.
      if (answered.current === key) return;
      answered.current = key;

      TenantApi.list()
        .then(({ tenants: all }) => {
          const { data: page, total: matched } = applyQuery(all, params);

          /*
           * `append` is the table telling us this page CONTINUES the list on
           * screen rather than replacing it — which is what the card view's
           * scroll asks for. Without it the grid would show only the newest
           * page and the cards above would vanish as the user scrolled. The
           * table view's pager does not set it, so paging still replaces.
           */
          accumulated.current = params.append ? [...accumulated.current, ...page] : page;

          setRows(accumulated.current);
          setTotal(matched);
          setCounts(countTenants(all));
          setPhase('ready');
        })
        .catch((error: unknown) => {
          console.error(error);
          setPhase('error');
        });
    },
    [setRows, setTotal, setCounts, setPhase],
  );
}

/**
 * Answers one `DataTableParams` query against the registry rows.
 *
 * Applies view filters, column-bound filters, free-text search, sort and
 * pagination locally. Kept pure so it is easy to replace with a real HTTP
 * call: `onFetch` stays the single data path.
 *
 * Exported for its unit cases: it is the page's whole query semantics, and the
 * cheapest place to pin filters/search/sort/slice is a pure function.
 */
export function applyQuery(rows: Tenant[], params: DataTableParams): {
  data: Tenant[];
  total: number;
} {
  let result = [...rows];

  // Apply view and column filters first.
  for (const [key, value] of Object.entries(params.filters)) {
    if (key === 'search') continue; // handled separately below
    result = result.filter((row) => String(row[key as keyof Tenant]) === value);
  }

  // Free-text search across slug and status.
  const search = params.filters.search;
  if (search) {
    const q = search.toLowerCase();
    result = result.filter(
      (row) =>
        String(row.slug).toLowerCase().includes(q) ||
        String(row.status).toLowerCase().includes(q),
    );
  }

  const total = result.length;

  if (params.sort.length > 0) {
    const term = params.sort[0]!;
    result.sort((a, b) => {
      const av = String(a[term.id as keyof Tenant]);
      const bv = String(b[term.id as keyof Tenant]);
      return term.desc ? bv.localeCompare(av) : av.localeCompare(bv);
    });
  }

  const start = (params.page - 1) * params.pageSize;
  return { data: result.slice(start, start + params.pageSize), total };
}

/**
 * A page-level action: export the current view as CSV. Stub for now.
 *
 * `secondary`, not `primary`: it sits beside "New tenant", and two filled
 * accent buttons in one row are indistinguishable — the eye cannot tell which
 * one is the page's main action, and the pair reads as a segmented control
 * rather than as two different verbs. The outlined treatment leaves exactly one
 * filled button on the page, so the primary action is unambiguous.
 *
 * The label is hidden below `sm` and the icon carries the button alone. Two
 * labelled buttons are ~232px of fixed width, which on a 320px phone leaves
 * under 60px for the page title — the title would truncate to a single letter.
 * Collapsing to the icon gives the title its full width back and keeps the
 * header on one row. The label stays as the accessible name via `aria-label`,
 * so the button is still announced as "Export CSV".
 */
function ExportButton(): ReactNode {
  return (
    <Button variant="secondary" size="sm" aria-label="Export CSV">
      <FiUpload size={14} aria-hidden="true" />
      <span className="hidden sm:inline">Export CSV</span>
    </Button>
  );
}

function NewTenantButton(): ReactNode {
  // The page header buttons are sized to match the controls in the data
  // table's filter row (search, column filter, Columns disclosure — all 32px
  // tall). The size is the same on mobile and desktop; mobile width is what
  // varies, not control height.
  //
  // The label is hidden below `sm` for the same reason as `ExportButton`: the
  // two buttons must not crowd the title off the row.
  return (
    <Button
      size="sm"
      aria-label="New tenant"
      onClick={() => globalThis.location?.assign('/platform/tenants/new')}
    >
      <FiPlus size={14} aria-hidden="true" />
      <span className="hidden sm:inline">New tenant</span>
    </Button>
  );
}

/**
 * Renders a tenant's status as a Badge so the row reads as a labelled status
 * rather than a raw enum value. Exported for the cards view, which uses the
 * same tone and label as the table.
 */
export function TenantStatusBadge({ status }: { status: Tenant['status'] }): ReactNode {
  const { tone, label } = STATUS_TONE[status];
  return <Badge tone={tone}>{label}</Badge>;
}
